import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Analytics Service - Track website visits and user behavior
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly apiUrl = `${environment.apiURL}/api/v1/Analytics`;
  private sessionId: string;

  constructor(private http: HttpClient) {
    this.sessionId = this.getOrCreateSessionId();
    // this.loadGoogleAnalytics();
    if (environment.enableGA) {
      this.loadGoogleAnalytics();
    }
  }

  /**
   * Track a page visit
   * @param pageUrl The URL of the page being visited
   * @param referrer The referrer URL (optional)
   */
  trackPageVisit(pageUrl: string, referrer?: string): Observable<any> {
    // // tránh gọi api tracking khi !environment.enableGA
    // if (!environment.enableGA) {
    //   return of(null);
    // }

    const gtagFn = this.getGtag();

    // GOOGLE ANALYTICS
    try {
      if (typeof gtagFn === 'function') {
        gtagFn('event', 'page_view', {
          page_path: pageUrl,
          page_location: window.location.href,
          page_title: document.title,
          ...(environment.enableGADebug && { debug_mode: true })
        });
      }
    } catch (err) {
      console.debug('GA tracking error:', err);
    }

    const trackData = {
      sessionId: this.sessionId,
      pageUrl: pageUrl,
      referrer: referrer || document.referrer,
      userAgent: navigator.userAgent,
      platform: 'Client' // Can be 'Client' or 'Dashboard'
    };

    // Send tracking data, but don't let it break the application if it fails
    return this.http.post(`${this.apiUrl}/track`, trackData).pipe(
      catchError(error => {
        // Log error silently without breaking the app
        console.debug('Analytics tracking failed:', error);
        return of(null);
      })
    );
  }
  private getGtag() {
    return (window as any).gtag;
  }
  /**
  * TRACK CUSTOM EVENT (GA)
  */
  trackEvent(eventName: string, params?: any): void {
    const gtagFn = this.getGtag();

    try {
      if (typeof gtagFn === 'function') {
        gtagFn('event', eventName, {
          ...params,
          ...(environment.enableGADebug && { debug_mode: true })
        });
      }
    } catch (err) {
      console.debug('GA event error:', err);
    }
  }

  /**
   * TRACK LOGIN (example)
   */
  trackLogin(method: string = 'normal') {
    this.trackEvent('login', {
      method: method
    });

  }

  /**
   * TRACK PURCHASE (example)
   */
  trackPurchase(orderId: string, value: number, items: any[] = []) {
    this.trackEvent('purchase', {
      transaction_id: orderId,
      value: value,         // Tổng giá trị đơn hàng
      currency: 'VND',      // Phải có đơn vị tiền tệ
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity
      }))
    });
  }


  /**
   * Get or create a session ID for tracking unique visitors
   * Stored in localStorage to persist across page reloads
   */
  private getOrCreateSessionId(): string {
    const storageKey = 'analytics_session_id';
    let sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      // Generate a unique session ID
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  }

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Reset the session ID (useful for testing or when user logs out)
   */
  resetSessionId(): void {
    localStorage.removeItem('analytics_session_id');
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
  * Định danh người dùng để tránh trùng lặp dữ liệu trên nhiều thiết bị
  * @param userId ID duy nhất từ Database của bạn
  */
  setUserId(userId: string | null): void {
    const gtagFn = this.getGtag();
    if (typeof gtagFn === 'function') {
      //Sử dụng lệnh 'config' để thiết lập user_id cho toàn bộ session
      gtagFn('config', environment.googleAnalyticsId, {
        'user_id': userId,
        'send_page_view': false // Ngăn việc gửi thêm 1 page_view thừa khi set ID
      });
      console.debug('GA: Đã thiết lập UserID:', userId);
    }
  }

  // load google analytics
  private loadGoogleAnalytics(): void {
    if (!environment.enableGA || !environment.googleAnalyticsId) return;

    if (document.getElementById('ga-script')) return;

    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
    document.head.appendChild(script);

    // init GA
    (window as any).dataLayer = (window as any).dataLayer || [];

    (window as any).gtag = function () {
      (window as any).dataLayer.push(arguments);
    };

    // gọi qua window
    (window as any).gtag('js', new Date());

    (window as any).gtag('config', environment.googleAnalyticsId, {
      send_page_view: false,
      ...(environment.enableGADebug && { debug_mode: true })
    });
  }
}

